import type {
  ClusterUpdateFormData,
  ClusterUpdateWithOptionalProductId,
} from '@/contexts/useConfigCreateRequest'
import {
  ExtensionStatus,
  ExtensionStatusState,
} from '@/features/extensions/ExtensionStatus'
import { KafkaExtensionPage } from '@/features/extensions/KafkaExtension/KafkaExtensionPage'
import { KinesisExtensionPage } from '@/features/extensions/KinesisExtension/KinesisExtensionPage'
import {
  KafkaFlavor,
  prepareKafkaConfigurationFormData,
} from '@/features/extensions/utils/kafkaUtils'
import type {
  KafkaExtensionConfiguration,
  KinesisExtensionConfiguration,
} from '@/generated/http-clients/v3ApiSchemas'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  toaster,
} from '@/hooks/useToast/toastUtils'
import {
  Accordion,
  Box,
  Button,
  Field,
  RadioGroup,
  Stack,
  Tabs,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

export enum ExtensionType {
  KAFKA = 'kafka',
  KINESIS = 'kinesis',
}

enum ExtensionTabIndex {
  KAFKA = 0,
  KINESIS = 1,
}

export interface NewClusterStep2FeaturesProps {
  configUpdateBody: ClusterUpdateWithOptionalProductId
  onChange: <Key extends keyof ClusterUpdateFormData>(
    name: Key,
    value: ClusterUpdateFormData[Key],
  ) => void
  setKafkaConfigHasBeenSaved?: React.Dispatch<React.SetStateAction<boolean>>
  initialExtensionTab?: ExtensionType
}

export const NewClusterStep2Features: React.FC<
  NewClusterStep2FeaturesProps
> = ({
  configUpdateBody,
  onChange,
  setKafkaConfigHasBeenSaved,
  initialExtensionTab = ExtensionType.KAFKA,
}) => {
  // useSuspense is false to avoid loading the translations for the entire app
  const { t } = useTranslation('features', { useSuspense: false })
  const [kafkaFlavor, setKafkaFlavor] = React.useState<KafkaFlavor>(
    KafkaFlavor.GENERIC,
  )
  const [displaySaveMessage, setDisplaySaveMessage] = React.useState(false)

  // Determine the initial tab index based on the provided extension type
  const initialTabIndex = React.useMemo(() => {
    if (initialExtensionTab === ExtensionType.KINESIS) {
      return ExtensionTabIndex.KINESIS
    }
    return ExtensionTabIndex.KAFKA
  }, [initialExtensionTab])

  // Function to update the Kafka config saved state
  const updateKafkaConfigSavedState = React.useCallback(
    (value: boolean) => {
      if (setKafkaConfigHasBeenSaved) {
        setKafkaConfigHasBeenSaved(value)
      }
    },
    [setKafkaConfigHasBeenSaved],
  )

  /**
   * Prepare the Kafka configuration with one mapping in each direction and all
   * necessary mapping IDs. It differs from
   * configUpdateBody.customer.extensions.kafka as it might not have all
   * necessary IDs. It needs to have an empty mapping array when a mapping isn't
   * defined to support unidirectional mappings.
   */
  const kafkaConfigWithAllMappingIDs: KafkaExtensionConfiguration | undefined =
    React.useMemo(() => {
      if (!configUpdateBody.customer?.extensions?.kafka) {
        return undefined
      }
      return prepareKafkaConfigurationFormData(
        configUpdateBody.customer.extensions.kafka,
      )
    }, [configUpdateBody.customer?.extensions?.kafka])

  function removeKafkaConfig() {
    onChange('_kafkaExtension', undefined)
    updateKafkaConfigSavedState(false)
  }

  function removeKinesisConfig() {
    onChange('_kinesisExtension', undefined)
  }

  const handleKafkaExtensionSubmit = (
    value: KafkaExtensionConfiguration | undefined,
  ) => {
    try {
      onChange('_kafkaExtension', value)
      toaster.create({
        ...SUCCESS_TOAST_OPTION,
        title: 'Kafka Extension config added',
      })
      updateKafkaConfigSavedState(true)
      setDisplaySaveMessage(false)
    } catch (error) {
      console.error('Failed to validate the Kafka extension config', error)
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: 'Failed to convert the Kafka extension config',
      })
      updateKafkaConfigSavedState(false)
    }
  }

  const handleKinesisExtensionSubmit = (
    value: KinesisExtensionConfiguration | undefined,
  ) => {
    try {
      // Add a new field to the form data to store Kinesis config
      onChange('_kinesisExtension', value)
      toaster.create({
        ...SUCCESS_TOAST_OPTION,
        title: 'Kinesis Extension config added',
      })
    } catch (error) {
      console.error('Failed to validate the Kinesis extension config', error)
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: 'Failed to convert the Kinesis extension config',
      })
    }
  }

  // Always ensure the Extensions accordion item is open
  const accordionDefaultValue = React.useMemo(() => ['1'], [])

  // State to track the currently selected tab
  const [selectedTabIndex, setSelectedTabIndex] =
    React.useState(initialTabIndex)

  // Update selected tab when initialTabIndex changes
  React.useEffect(() => {
    setSelectedTabIndex(initialTabIndex)
  }, [initialTabIndex])

  return (
    <VStack
      data-testid="step-extensions"
      gap={6}
      alignItems="start"
      width="100%"
    >
      <Accordion.Root
        width="100%"
        collapsible
        defaultValue={accordionDefaultValue}
      >
        <Accordion.Item value="0" disabled>
          <Tooltip.Root positioning={{ placement: 'top-start' }}>
            <Tooltip.Trigger asChild>
              <Accordion.ItemTrigger>
                <Box as="span" flex="1" textAlign="left">
                  <Text fontWeight="bold">{t('authentication.title')}</Text>
                </Box>
                <Accordion.ItemIndicator />
              </Accordion.ItemTrigger>
            </Tooltip.Trigger>
            <Tooltip.Positioner>
              <Tooltip.Content>
                <Tooltip.Arrow />
                {t('authentication.futureRelease')}
              </Tooltip.Content>
            </Tooltip.Positioner>
          </Tooltip.Root>
          <Accordion.ItemContent>
            <Accordion.ItemBody pb={4}>
              <Text>{t('authentication.futureRelease')}</Text>
            </Accordion.ItemBody>
          </Accordion.ItemContent>
        </Accordion.Item>
        <Accordion.Item value="1">
          <Accordion.ItemTrigger>
            <Box as="span" flex="1" textAlign="left">
              <Text fontWeight="bold">{t('extensions.title')}</Text>
            </Box>
            <Accordion.ItemIndicator />
          </Accordion.ItemTrigger>
          <Accordion.ItemContent>
            <Accordion.ItemBody pb={4}>
              <Tabs.Root
                value={
                  selectedTabIndex === ExtensionTabIndex.KAFKA
                    ? 'kafka'
                    : 'kinesis'
                }
                onValueChange={(details) =>
                  setSelectedTabIndex(
                    details.value === 'kafka'
                      ? ExtensionTabIndex.KAFKA
                      : ExtensionTabIndex.KINESIS,
                  )
                }
                defaultValue="kafka"
              >
                <Tabs.List>
                  <Tabs.Trigger value="kafka">
                    {t('extensions.kafka')}
                  </Tabs.Trigger>
                  <Tabs.Trigger value="kinesis">
                    {t('extensions.kinesis.title')}
                  </Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="kafka" padding={4}>
                  <VStack mb={4} gap={4} alignItems="start">
                    <ExtensionStatus
                      status={
                        configUpdateBody.customer?.extensions?.kafka
                          ? ExtensionStatusState.CONFIGURED
                          : ExtensionStatusState.INACTIVE
                      }
                    />
                    {configUpdateBody.customer?.extensions?.kafka && (
                      <Button
                        variant="solid"
                        colorPalette="red"
                        onClick={removeKafkaConfig}
                      >
                        {t('extensions.removeConfiguration')}
                      </Button>
                    )}
                  </VStack>
                  <Field.Root key="kafkaFlavor" mb={4}>
                    <Field.Label>{t('extensions.kafkaType.title')}</Field.Label>
                    <RadioGroup.Root
                      onValueChange={(details) => {
                        setKafkaFlavor(details.value as KafkaFlavor)
                        updateKafkaConfigSavedState(false)
                        setDisplaySaveMessage(true)
                      }}
                      value={kafkaFlavor}
                    >
                      <Stack direction="row" gap={4}>
                        <RadioGroup.Item value={KafkaFlavor.GENERIC}>
                          <RadioGroup.ItemHiddenInput />
                          <RadioGroup.ItemIndicator />
                          <RadioGroup.ItemText>
                            {t('extensions.kafkaType.generic')}
                          </RadioGroup.ItemText>
                        </RadioGroup.Item>
                        <RadioGroup.Item value={KafkaFlavor.CONFLUENT}>
                          <RadioGroup.ItemHiddenInput />
                          <RadioGroup.ItemIndicator />
                          <RadioGroup.ItemText>
                            {t('extensions.kafkaType.confluent')}
                          </RadioGroup.ItemText>
                        </RadioGroup.Item>
                      </Stack>
                    </RadioGroup.Root>
                    {displaySaveMessage && (
                      <Field.HelperText color="text.text-warning">
                        {t('extensions.kafkaType.saveMessage')}
                      </Field.HelperText>
                    )}
                  </Field.Root>
                  <Field.Root key="kafkaExtensionConfiguration">
                    <KafkaExtensionPage
                      flavor={kafkaFlavor}
                      initialConfig={kafkaConfigWithAllMappingIDs}
                      onSubmit={handleKafkaExtensionSubmit}
                    />
                  </Field.Root>
                </Tabs.Content>
                <Tabs.Content value="kinesis" padding={4}>
                  <VStack mb={4} gap={4} alignItems="start">
                    <ExtensionStatus
                      status={
                        configUpdateBody.customer?.extensions?.kinesis
                          ? ExtensionStatusState.CONFIGURED
                          : ExtensionStatusState.INACTIVE
                      }
                    />
                    {configUpdateBody.customer?.extensions?.kinesis && (
                      <Button
                        variant="solid"
                        colorPalette="red"
                        onClick={removeKinesisConfig}
                      >
                        {t('extensions.removeConfiguration')}
                      </Button>
                    )}
                  </VStack>
                  <Field.Root key="kinesisExtensionConfiguration">
                    <KinesisExtensionPage
                      initialConfig={
                        configUpdateBody.customer?.extensions?.kinesis
                      }
                      onSubmit={handleKinesisExtensionSubmit}
                    />
                  </Field.Root>
                </Tabs.Content>
              </Tabs.Root>
            </Accordion.ItemBody>
          </Accordion.ItemContent>
        </Accordion.Item>
      </Accordion.Root>
    </VStack>
  )
}
